import { Timeline } from '@/components/common/TimeLine';
import { DISPPLAY_MOMENT_YMD_HM, ekDomain, ROUTES } from '@/configs/constants';
import { ORDER_STATUS, OrderStatusType, PAYMENT_TYPE } from '@/types/enums';
import { PaymentHistoryItem, ReservationHistoryTime } from '@/types/reservation';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { cn } from '@/utils/utils';
import { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

interface OrderTimeLineProps {
    historyTime: ReservationHistoryTime,
    orderStatus: OrderStatusType,
    isEnduser: boolean,
    paymentHistory: PaymentHistoryItem[]
}
export default observer(function OrderTimeLine({ historyTime, paymentHistory, orderStatus, isEnduser = false }: OrderTimeLineProps) {
    // hook
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);
    const { id } = useParams();

    return (
        <div className='flex flex-col gap-4'>
            {/* <h1 className='text-[1.125rem] text-black-1 font-semibold md:text-2xl'>{t('words_title.time_line')}</h1> */}
            <Timeline events={modifyHistoryTime(historyTime, paymentHistory, orderStatus, isEnduser, isEk, id, t)} />
        </div>
    );
});

const getLinkReportHistory = (isEnduser: boolean, isEk: boolean, id: string | undefined) => {
    if (isEk) {
        return isEnduser ?
            `/${ekDomain}${ROUTES.reservationReportHistoryEnduser.href}/${id}` :
            `/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationReportHistory.href}/${id}`;
    } else {
        return isEnduser ?
            `${ROUTES.reservationReportHistoryEnduser.href}/${id}` :
            `${ROUTES.reservationManagement.href}/${ROUTES.reservationReportHistory.href}/${id}`;
    }
};
const getLinkReturnHistory = (isEnduser: boolean, isEk: boolean, id: string | undefined) => {
    if (isEk) {
        return isEnduser ?
            `/${ekDomain}${ROUTES.reservationReturnHistoryEnduser.href}/${id}` :
            `/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationReturnHistory.href}/${id}`;
    } else {
        return isEnduser ?
            `${ROUTES.reservationReturnHistoryEnduser.href}/${id}` :
            `${ROUTES.reservationManagement.href}/${ROUTES.reservationReturnHistory.href}/${id}`;
    }
};

const modifyHistoryTime = (historyTime: ReservationHistoryTime, paymentAllHistory: PaymentHistoryItem[], status: OrderStatusType, isEnduser: boolean, isEk: boolean, id: string | undefined, t: TFunction) => {
    let timeLine: any[] = [];

    let paymentHistory = paymentAllHistory.filter(item => [PAYMENT_TYPE.ORDER_CREATE, PAYMENT_TYPE.UPDATE_ORDER].includes(item.paymentType) && Number(item.amount) > 0);
    if (paymentHistory && paymentHistory.length > 0) {
        // timelineMarkers.push({
        //     marker: 'Create',
        //     milestoneDate: paymentHistory[0].createdDate
        // });
        timeLine = [...timeLine, {
            icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
            content: {
                children: (
                    <OrderContentItem title={t('sentences.reservation_create')} date={paymentHistory[0].createdDate} />
                )
            }
        }];
        let groupMaxPaid = paymentHistory[0].paymentDate;

        if (paymentHistory.length > 1) {
            for (let i = 1; i < paymentHistory.length; i++) {
                const curr = paymentHistory[i];

                if (curr.createdDate > groupMaxPaid) {
                    // Payment mới được tạo sau mốc của nhóm cũ → chuyển nhóm mới
                    // Marker là "Paid" và mốc lấy từ payment_date (nếu có) hoặc từ create_date nếu không có
                    // timelineMarkers.push({
                    //     marker: 'Paid',
                    //     milestoneDate: groupMaxPaid
                    // });

                    timeLine = [...timeLine, {
                        icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
                        content: {
                            children: (
                                <OrderContentItem title={t('sentences.already_paid')} date={groupMaxPaid} />
                            )
                        }
                    }];

                    timeLine.push({
                        icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
                        content: {
                            children: (
                                <OrderContentItem
                                    title={t('words_title.add_not_paid')}
                                    date={curr.createdDate}
                                />
                            )
                        }
                    });
                    // timelineMarkers.push({
                    //     marker: 'Added not paid',
                    //     milestoneDate: curr.createdDate
                    // });
                    // Cập nhật mốc nhóm nếu có payment_date, nếu không giữ nguyên
                    if (curr.paymentDate) {
                        groupMaxPaid = curr.paymentDate;
                        if (i === (paymentHistory.length - 1)) {
                            // timelineMarkers.push({
                            //     marker: 'Paid',
                            //     milestoneDate: curr.paymentDate
                            // });
                            timeLine = [...timeLine, {
                                icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
                                content: {
                                    children: (
                                        <OrderContentItem title={t('sentences.already_paid')} date={curr.paymentDate} />
                                    )
                                }
                            }];
                        }
                    }
                } else {
                    // Payment nằm trong cùng nhóm (curr.create_date ≤ groupMaxPaid)
                    if (curr.paymentDate) {
                        if (curr.paymentDate <= groupMaxPaid) {
                            // Payment được trả nhưng không vượt mốc cũ
                            if (i === (paymentHistory.length - 1)) {
                                // timelineMarkers.push({
                                //     marker: 'Paid',
                                //     milestoneDate: groupMaxPaid
                                // });
                                timeLine = [...timeLine, {
                                    icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
                                    content: {
                                        children: (
                                            <OrderContentItem title={t('sentences.already_paid')} date={groupMaxPaid} />
                                        )
                                    }
                                }];
                            }
                            // groupMaxPaid vẫn giữ nguyên
                        } else if (curr.paymentDate > groupMaxPaid) {
                            // Payment trả sau mốc hiện hành → có sự chênh lệch: marker là "(Added not paid)"
                            // timelineMarkers.push({
                            //     marker: 'Paid',
                            //     milestoneDate: curr.paymentDate
                            // });
                            timeLine = [...timeLine, {
                                icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
                                content: {
                                    children: (
                                        <OrderContentItem title={t('sentences.already_paid')} date={curr.paymentDate} />
                                    )
                                }
                            }];
                            groupMaxPaid = curr.paymentDate; // cập nhật mốc mới
                        }
                    } else {
                        // Payment hiện tại chưa có payment_date → marker là "(Added not paid)"
                        // timelineMarkers.push({
                        //     marker: 'Added not paid',
                        //     milestoneDate: curr.createdDate
                        // });
                        timeLine.push({
                            icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
                            content: {
                                children: (
                                    <OrderContentItem
                                        title={t('words_title.add_not_paid')}
                                        date={curr.createdDate}
                                    />
                                )
                            }
                        });
                    }
                }
            }
        } else {
            if (groupMaxPaid) {
                // timelineMarkers.push({
                //     marker: 'Paid',
                //     milestoneDate: groupMaxPaid
                // });
                timeLine = [...timeLine, {
                    icon: { children: '', className: 'size-7 bg-[#0CC763] border-[0.25rem] border-[#DBF7E8]' },
                    content: {
                        children: (
                            <OrderContentItem title={t('sentences.already_paid')} date={groupMaxPaid} />
                        )
                    }
                }];
            }
        }
    }

    if (historyTime.completeVehicleDate) {
        timeLine = [...timeLine, {
            icon: { children: '', className: 'size-7 bg-[#F26202] border-[0.25rem] border-[#DBF7E8]' },
            content: {
                children: (
                    <OrderContentItem title={t('sentences.report_bike')} date={historyTime.completeVehicleDate} link={getLinkReportHistory(isEnduser, isEk, id)} />
                )
            }
        }];
    }
    const statusList: OrderStatusType[] = [ORDER_STATUS.Return, ORDER_STATUS.Overdue];
    if (statusList.includes(status)) {
        if (status === ORDER_STATUS.Overdue) {
            timeLine = [...timeLine, {
                icon: { children: '', className: 'size-7 bg-[#FF1111] border-[0.25rem] border-[#DBF7E8]' },
                content: {
                    children: (
                        <OrderContentItem title={t('sentences.overdue')} date={historyTime.bikeOverdueDate} />
                    )
                }
            }];
        }
        if (status === ORDER_STATUS.Return) {
            if (historyTime.returnDate) {
                const returnDate = new Date(historyTime.returnDate);
                const bikeOverdueDate = new Date(historyTime.bikeOverdueDate);
                if (returnDate.getTime() > bikeOverdueDate.getTime()) {
                    timeLine = [...timeLine, {
                        icon: { children: '', className: 'size-7 bg-[#FF1111] border-[0.25rem] border-[#DBF7E8]' },
                        content: {
                            children: (
                                <OrderContentItem title={t('sentences.overdue')} titleClass='text-red-5' dateClass='text-[#ED4448]' date={historyTime.bikeOverdueDate} />
                            )
                        }
                    }];
                }
                timeLine = [...timeLine, {
                    icon: { children: '', className: 'size-7 bg-[#3B7CEC] border-[0.25rem] border-[#DBF7E8]' },
                    content: {
                        children: (
                            <OrderContentItem title={t('sentences.return_bike')} date={historyTime.returnDate} link={getLinkReturnHistory(isEnduser, isEk, id)} />
                        )
                    }
                }];
            }
        }
    }

    if (historyTime.cancelDate) {
        let title = t('sentences.cancel');
        if (historyTime.cancelBy) {
            title = title.replace('{{name}}', t(`cancel_by.${historyTime.cancelBy.toLowerCase()}`));
        }
        timeLine = [...timeLine, {
            icon: { children: '', className: 'size-7 bg-[#FF1111] border-[0.25rem] border-[#DBF7E8]' },
            content: {
                children: (
                    <OrderContentItem title={title} titleClass='text-red-5' dateClass='text-[#ED4448]' date={historyTime.cancelDate} />
                )
            }
        }];
    }

    return timeLine;
};

interface OrderContentItemProps {
    title: string,
    link?: string,
    date?: string,
    titleClass?: string,
    dateClass?: string
}
function OrderContentItem({ title, link, date, titleClass, dateClass }: OrderContentItemProps) {
    // hooks
    const { t } = useTranslation('transalation', { keyPrefix: 'field.reservation' });
    const { pathname } = useLocation();
    return (
        <div className='w-full font-medium'>
            <h3 className={cn('text-black-1', titleClass)}>{title}</h3>
            <p className={cn('text-[#B8B6B6]', dateClass)}>{formatDateTime(date, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</p>
            {link && <Link to={link} state={{ previousUrl: pathname }} className='text-blue-500 hoverable:hover:underline' title={t('view_detail')}>{t('view_detail')}</Link>}
        </div>
    );
}
